import React, { useState, useEffect } from 'react';
import { Modal, Spin, message, Empty, Drawer, Descriptions, Tag, Typography, Tabs } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import ObservatoryService from '../services/ObservatoryService';
import SimpleJsonViewer from './SimpleJsonViewer';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

/**
 * TraceTimelineModal - Modal component for displaying trace timeline with span hierarchy
 * 
 * @param {Object} props
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {Object} props.trace - The root trace object
 * @param {function} props.onClose - Callback when modal is closed
 */
const TraceTimelineModal = ({ visible, trace, onClose }) => {
  const [spans, setSpans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSpan, setSelectedSpan] = useState(null);
  const [spanDetail, setSpanDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Load spans when modal opens
  useEffect(() => {
    if (visible && trace && trace.trace_id) {
      loadTraceSpans();
    } else {
      // Reset state when modal closes
      setSpans([]);
      setSelectedSpan(null);
      setSpanDetail(null);
      setDrawerVisible(false);
    }
  }, [visible, trace]);

  const loadTraceSpans = async () => {
    try {
      setLoading(true);
      const data = await ObservatoryService.getTraceSpans(trace.trace_id);
      setSpans(data.spans || []);
    } catch (error) {
      console.error('Error loading trace spans:', error);
      message.error(`Failed to load trace spans: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSpanClick = async (span) => {
    try {
      setSelectedSpan(span);
      setDetailLoading(true);
      setDrawerVisible(true);
      
      const detail = await ObservatoryService.getSpanDetail(span.span_id);
      setSpanDetail(detail);
    } catch (error) {
      console.error('Error loading span detail:', error);
      message.error(`Failed to load span detail: ${error.message}`);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedSpan(null);
    setSpanDetail(null);
  };

  // Build hierarchical structure from flat span list
  const buildHierarchy = (spansList) => {
    if (!spansList || spansList.length === 0) return [];
    
    // Create a map for quick lookup
    const spanMap = new Map();
    spansList.forEach(span => {
      spanMap.set(span.span_id, { ...span, children: [] });
    });
    
    // Build tree structure
    const roots = [];
    spansList.forEach(span => {
      const spanNode = spanMap.get(span.span_id);
      if (!span.parent_span_id) {
        roots.push(spanNode);
      } else {
        const parent = spanMap.get(span.parent_span_id);
        if (parent) {
          parent.children.push(spanNode);
        } else {
          // Parent not found, treat as root
          roots.push(spanNode);
        }
      }
    });
    
    return roots;
  };

  // Calculate position for timeline bar
  const calculateTimelinePosition = (span, rootStartTime) => {
    if (!span.start_time || !rootStartTime) return { left: 0, width: 100 };
    
    const spanStart = new Date(span.start_time).getTime();
    const rootStart = new Date(rootStartTime).getTime();
    const offset = spanStart - rootStart;
    
    return {
      offset: offset,
      duration: span.latency_ms || 0
    };
  };

  // Get color based on span characteristics
  const getSpanColor = (span) => {
    if (span.status === 'failed') return '#f5222d'; // Red for failed
    if (span.has_llm_call) return '#fa8c16'; // Orange for LLM calls
    if (span.method_name && span.method_name.includes('Graph')) return '#1890ff'; // Blue for graphs
    if (span.method_name && span.method_name.includes('retrieve')) return '#162349'; // Purple for retrievers
    return '#389e0d'; // Darker green for others (was #52c41a)
  };

  // Format latency
  const formatLatency = (ms) => {
    if (ms === null || ms === undefined) return 'N/A';
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // Render a single span row with timeline bar
  const renderSpan = (span, level, rootStartTime, totalDuration) => {
    const color = getSpanColor(span);
    const position = calculateTimelinePosition(span, rootStartTime);
    
    // Calculate bar position and width as percentage
    const leftPercent = (position.offset / totalDuration) * 100;
    const widthPercent = (position.duration / totalDuration) * 100;
    
    return (
      <div key={span.span_id}>
        <div
          onClick={() => handleSpanClick(span)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            cursor: 'pointer',
            backgroundColor: selectedSpan?.span_id === span.span_id ? '#e6f7ff' : 'transparent',
            borderLeft: selectedSpan?.span_id === span.span_id ? '3px solid #1890ff' : 'none',
            ':hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
          onMouseEnter={(e) => {
            if (selectedSpan?.span_id !== span.span_id) {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedSpan?.span_id !== span.span_id) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          {/* Left side - Method name with indentation */}
          <div style={{ 
            width: '40%', 
            paddingLeft: `${level * 20}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            <span style={{ 
              color: color,
              fontWeight: level === 0 ? 'bold' : 'normal',
              fontSize: '13px'
            }}>
              {(span.has_llm_call && span.purpose) ? span.purpose : (span.method_name || 'Unknown')}
            </span>
            {span.has_llm_call && (
              <Tag color="orange" style={{ marginLeft: '8px', fontSize: '10px' }}>LLM</Tag>
            )}
          </div>
          
          {/* Middle - Timeline bar */}
          <div style={{ 
            width: '50%', 
            position: 'relative',
            height: '24px',
            backgroundColor: '#F4F4F4',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div
              style={{
                position: 'absolute',
                left: `${leftPercent}%`,
                width: `${Math.max(widthPercent, 1)}%`,
                height: '100%',
                backgroundColor: color,
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              {widthPercent > 5 && formatLatency(span.latency_ms)}
            </div>
          </div>
          
          {/* Right side - Latency text */}
          <div style={{ 
            width: '10%', 
            textAlign: 'right',
            fontSize: '12px',
            color: '#666'
          }}>
            {formatLatency(span.latency_ms)}
          </div>
        </div>
        
        {/* Render children recursively */}
        {span.children && span.children.map(child => 
          renderSpan(child, level + 1, rootStartTime, totalDuration)
        )}
      </div>
    );
  };

  // Render the timeline
  const renderTimeline = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" tip="Loading trace timeline..." />
        </div>
      );
    }

    if (spans.length === 0) {
      return <Empty description="No spans found for this trace" />;
    }

    const hierarchy = buildHierarchy(spans);
    if (hierarchy.length === 0) {
      return <Empty description="Unable to build trace hierarchy" />;
    }

    // Get root span for timeline calculations
    const rootSpan = hierarchy[0];
    const rootStartTime = rootSpan.start_time;
    const totalDuration = spans.reduce((max, span) => {
      const pos = calculateTimelinePosition(span, rootStartTime);
      return Math.max(max, pos.offset + pos.duration);
    }, 0);

    return (
      <div style={{ 
        backgroundColor: 'white',
        border: '1px solid #d9d9d9',
        borderRadius: '4px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #d9d9d9',
          fontWeight: 'bold',
          fontSize: '12px'
        }}>
          <div style={{ width: '40%' }}>Method Name</div>
          <div style={{ width: '50%' }}>Timeline</div>
          <div style={{ width: '10%', textAlign: 'right' }}>Duration</div>
        </div>
        
        {/* Color Legend */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#F4F4F4',
          borderBottom: '1px solid #d9d9d9',
          fontSize: '11px',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Legend:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#fa8c16', borderRadius: '2px' }}></div>
            <span>LLM Call</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#1890ff', borderRadius: '2px' }}></div>
            <span>Graph</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#162349', borderRadius: '2px' }}></div>
            <span>Retriever</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#389e0d', borderRadius: '2px' }}></div>
            <span>Other</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#f5222d', borderRadius: '2px' }}></div>
            <span>Failed</span>
          </div>
        </div>
        
        {/* Span rows */}
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {hierarchy.map(rootSpan => renderSpan(rootSpan, 0, rootStartTime, totalDuration))}
        </div>
      </div>
    );
  };

  // Render span detail drawer
  const renderSpanDetailDrawer = () => {
    return (
      <Drawer
        title={
          <div>
            <Title level={5} style={{ margin: 0 }}>Span Details</Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {selectedSpan?.method_name || 'Unknown Method'}
            </Text>
          </div>
        }
        placement="right"
        width={600}
        onClose={handleDrawerClose}
        visible={drawerVisible}
        closeIcon={<CloseOutlined />}
        zIndex={1100}
        maskClosable={true}
        destroyOnClose={true}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" tip="Loading span details..." />
          </div>
        ) : spanDetail ? (
          <Tabs defaultActiveKey="overview">
            <TabPane tab="Overview" key="overview">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Span ID">
                  <Text copyable style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                    {spanDetail.span_id}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Method">{spanDetail.method_name || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={spanDetail.status === 'completed' ? 'success' : spanDetail.status === 'failed' ? 'error' : 'processing'}>
                    {spanDetail.status?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Latency">{formatLatency(spanDetail.latency_ms)}</Descriptions.Item>
                <Descriptions.Item label="Start Time">
                  {spanDetail.start_time ? new Date(spanDetail.start_time).toLocaleString() : 'N/A'}
                </Descriptions.Item>
                {spanDetail.error_message && (
                  <Descriptions.Item label="Error">
                    <Text type="danger">{spanDetail.error_message}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </TabPane>
            
            {spanDetail.params_snapshot && (
              <TabPane tab="Parameters" key="params">
                <SimpleJsonViewer 
                  data={spanDetail.params_snapshot} 
                  name="params_snapshot"
                  collapsed={1}
                />
              </TabPane>
            )}
            
            {spanDetail.domain_metadata && (
              <TabPane tab="Domain Metadata" key="domain">
                <SimpleJsonViewer 
                  data={spanDetail.domain_metadata} 
                  name="domain_metadata"
                  collapsed={1}
                />
              </TabPane>
            )}
            
            {spanDetail.node_result && (
              <TabPane tab="Node Result" key="result">
                <SimpleJsonViewer 
                  data={spanDetail.node_result} 
                  name="node_result"
                  collapsed={1}
                />
              </TabPane>
            )}
            
            {spanDetail.llm_call && (
              <TabPane tab="LLM Call" key="llm">
                <Descriptions column={1} size="small" bordered style={{ marginBottom: '16px' }}>
                  <Descriptions.Item label="Model">{spanDetail.llm_call.model_name}</Descriptions.Item>
                  <Descriptions.Item label="Context">{spanDetail.llm_call.llm_call_context}</Descriptions.Item>
                  <Descriptions.Item label="Input Tokens">{spanDetail.llm_call.input_tokens?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Output Tokens">{spanDetail.llm_call.output_tokens?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Total Tokens">{spanDetail.llm_call.total_tokens?.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Input Cost">${spanDetail.llm_call.input_cost?.toFixed(4)}</Descriptions.Item>
                  <Descriptions.Item label="Output Cost">${spanDetail.llm_call.output_cost?.toFixed(4)}</Descriptions.Item>
                  <Descriptions.Item label="Total Cost">${spanDetail.llm_call.total_cost?.toFixed(4)}</Descriptions.Item>
                  <Descriptions.Item label="Latency">{formatLatency(spanDetail.llm_call.latency_ms)}</Descriptions.Item>
                </Descriptions>
                
                {spanDetail.llm_call.prompt_params && (
                  <div style={{ marginBottom: '16px' }}>
                    <Title level={5}>Prompt Parameters</Title>
                    <SimpleJsonViewer 
                      data={spanDetail.llm_call.prompt_params} 
                      name="prompt_params"
                      collapsed={1}
                    />
                  </div>
                )}
                
                {spanDetail.llm_call.prompt_text && (
                  <div style={{ marginBottom: '16px' }}>
                    <Title level={5}>Prompt Text</Title>
                    <div style={{
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {spanDetail.llm_call.prompt_text}
                    </div>
                  </div>
                )}
                
                {spanDetail.llm_call.output_text && (
                  <div>
                    <Title level={5}>Output Text</Title>
                    <div style={{
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {spanDetail.llm_call.output_text}
                    </div>
                  </div>
                )}
              </TabPane>
            )}
          </Tabs>
        ) : (
          <Empty description="No span detail available" />
        )}
      </Drawer>
    );
  };

  return (
    <>
      <Modal
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>Trace Timeline</Title>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              <Text type="secondary">Trace ID: </Text>
              <Text copyable style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                {trace?.trace_id}
              </Text>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <Text type="secondary">Agent: </Text>
              <Text strong>{trace?.agent_name || 'N/A'}</Text>
              <Text type="secondary" style={{ marginLeft: '16px' }}>Type: </Text>
              <Text>{trace?.agent_type || 'N/A'}</Text>
            </div>
          </div>
        }
        visible={visible}
        onCancel={onClose}
        width="90%"
        style={{ top: 20 }}
        footer={null}
        destroyOnClose
        zIndex={1000}
        maskClosable={false}
      >
        {renderTimeline()}
      </Modal>
      
      {renderSpanDetailDrawer()}
    </>
  );
};

export default TraceTimelineModal;